import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    ArrowRight,
    Clock,
    Globe,
    TrendingUp,
    BarChart3,
    Coins,
    Bitcoin,
    AlertCircle,
} from 'lucide-react';

interface MarketHours {
    market: string;
    icon: React.ElementType;
    hours: string;
    days: string;
    sessions: string[];
    note?: string;
}

const markets: MarketHours[] = [
    {
        market: 'Forex',
        icon: Globe,
        hours: '24 Hours',
        days: 'Monday - Friday',
        sessions: [
            'Sydney: 10:00 PM - 7:00 AM GMT',
            'Tokyo: 12:00 AM - 9:00 AM GMT',
            'London: 8:00 AM - 5:00 PM GMT',
            'New York: 1:00 PM - 10:00 PM GMT',
        ],
        note: 'Market opens Sunday 10:00 PM GMT, closes Friday 10:00 PM GMT',
    },
    {
        market: 'Indices',
        icon: BarChart3,
        hours: 'Session-Based',
        days: 'Monday - Friday',
        sessions: [
            'US30 / NAS100 / SPX500: 11:00 PM - 10:00 PM GMT (23hrs)',
            'GER40: 1:15 AM - 10:00 PM GMT',
            'UK100: 8:00 AM - 9:00 PM GMT',
            'JPN225: 12:00 AM - 9:00 AM GMT',
        ],
        note: 'Daily maintenance breaks may apply',
    },
    {
        market: 'Commodities',
        icon: Coins,
        hours: 'Session-Based',
        days: 'Monday - Friday',
        sessions: [
            'Gold (XAUUSD): 11:00 PM - 10:00 PM GMT (23hrs)',
            'Silver (XAGUSD): 11:00 PM - 10:00 PM GMT (23hrs)',
            'Oil (WTI): 11:00 PM - 10:00 PM GMT (23hrs)',
            'Natural Gas: 11:00 PM - 10:00 PM GMT (23hrs)',
        ],
        note: 'Daily settlement break 10:00 PM - 11:00 PM GMT',
    },
    {
        market: 'Crypto',
        icon: Bitcoin,
        hours: '24 Hours',
        days: '7 Days a Week',
        sessions: [
            'BTC/USD: 24/7',
            'ETH/USD: 24/7',
            'LTC/USD: 24/7',
            'Other crypto pairs: 24/7',
        ],
        note: 'Crypto markets never close — trade anytime',
    },
];

export default function TradingHours() {
    return (
        <AppLayout title={`Trading Hours - ${siteConfig.brand.name}`} description={`View trading hours for all markets available on the ${siteConfig.brand.name} platform.`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Markets</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                            Trading <span className="text-gradient">Hours</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Know when to trade. View the operating hours for all markets available on the {siteConfig.brand.name}
                            platform, from forex to crypto.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Market Hours Grid */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Market"
                        highlight="Schedule"
                        description="Trading hours for each asset class. All times are displayed in GMT."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        {markets.map((market) => {
                            const Icon = market.icon;
                            return (
                                <motion.div key={market.market} variants={staggerItem}>
                                    <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-0 h-full">
                                        <CardContent className="p-6 pt-6">
                                            <div className="flex items-center gap-4 mb-5">
                                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                    <Icon className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h3 className="text-xl font-bold text-foreground">
                                                        {market.market}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        {market.hours} &middot; {market.days}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="space-y-2 mb-4">
                                                {market.sessions.map((session) => (
                                                    <div
                                                        key={session}
                                                        className="flex items-center gap-3 text-sm"
                                                    >
                                                        <Clock className="h-3.5 w-3.5 text-primary shrink-0" />
                                                        <span className="text-muted-foreground">{session}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {market.note && (
                                                <div className="flex items-start gap-2 rounded-lg bg-muted/50 border border-border p-3">
                                                    <AlertCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                                    <p className="text-xs text-muted-foreground">{market.note}</p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Timezone Note */}
            <Section className="bg-background">
                <FadeIn>
                    <div className="max-w-4xl mx-auto">
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm p-0">
                            <CardContent className="p-8 pt-8">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <TrendingUp className="h-5 w-5 text-primary" />
                                    </div>
                                    <h3 className="text-xl font-bold text-foreground">Important Notes</h3>
                                </div>
                                <ul className="space-y-3">
                                    <li className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed">
                                        <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-2" />
                                        All times shown are in <strong className="text-foreground">GMT (Greenwich Mean Time)</strong>. Please adjust for your local timezone.
                                    </li>
                                    <li className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed">
                                        <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-2" />
                                        Trading hours may be affected by daylight saving time changes in various regions.
                                    </li>
                                    <li className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed">
                                        <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-2" />
                                        Spreads may widen during market open/close and around major economic news releases.
                                    </li>
                                    <li className="flex items-start gap-3 text-muted-foreground text-sm leading-relaxed">
                                        <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-2" />
                                        Some instruments may have reduced liquidity during off-peak hours. Plan your trades accordingly.
                                    </li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                            Ready to Start <span className="text-gradient">Trading?</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Access all these markets and more with a {siteConfig.brand.name} funded account. Get started today.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Funded
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </a>
                            <Link to="/funding/overview">
                                <Button variant="outline" size="lg">
                                    View Programs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
